import { Field, useFormikContext } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SquareFormValues } from '@/utils/forms/squareTransformers';
import { Button } from '@/components/ui/button';
import { Upload, CheckCircle2 } from 'lucide-react';
import { JerseyPickerButton } from '@/components/composite/JerseyPickerButton';
import { CustomAssetsSection } from './CustomAssetsSection';
import { useEffect, useRef } from 'react';
import { MAX_UPLOAD_SIZE_MB, MAX_UPLOAD_SIZE_BYTES } from '@low6-pickem/ui';
import { resolveTeamImageUrl, type Sport } from '@/utils/jersey-mapping';
import { getTeamPrimaryColor } from '@/utils/teamColors';

interface TeamStylingSectionProps {
  disabled?: boolean;
  contestId?: string; // Squares contest ID for uploading assets
  tenantCode?: string;
}

export function TeamStylingSection({
  disabled = false,
  contestId,
  tenantCode,
}: TeamStylingSectionProps) {
  const { values, setFieldValue } = useFormikContext<SquareFormValues>();
  const rowTeamFileInputRef = useRef<HTMLInputElement>(null);
  const colTeamFileInputRef = useRef<HTMLInputElement>(null);
  const autoLoadTeamAssets = values.auto_team_assets_from_matchup ?? true;

  const prevRowAbbr = useRef<string | null>(null);
  const prevColAbbr = useRef<string | null>(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!autoLoadTeamAssets || !values.sport) return;

    const isFirst = !isInitialized.current;
    const prevRow = prevRowAbbr.current;
    const prevCol = prevColAbbr.current;

    prevRowAbbr.current = values.row_team_abbreviation ?? null;
    prevColAbbr.current = values.col_team_abbreviation ?? null;
    isInitialized.current = true;

    // On abbreviation change: always repopulate. On first mount: populate only empty fields
    // (avoids overwriting manual customisations on existing contests).
    const rowAbbrChanged = !isFirst && values.row_team_abbreviation !== prevRow;
    const colAbbrChanged = !isFirst && values.col_team_abbreviation !== prevCol;

    // Skip the "TBD" placeholder (teams-TBD contests created before the matchup was known) —
    // synthesizing a logo from it produces a broken `.../teams/TBD.png` URL.
    if (values.row_team_abbreviation && values.row_team_abbreviation.toUpperCase() !== 'TBD') {
      const populateIcon = rowAbbrChanged || (isFirst && !values.row_team_icon_url);
      const populateColor = rowAbbrChanged || (isFirst && !values.row_team_bg_color);
      if (populateIcon) {
        // League IP: licensed logo when on, generic team avatar when off
        // (resolver falls back to the logo for sports with no avatar, e.g. NHL/NCAAMB).
        const url = resolveTeamImageUrl({
          teamAbbr: values.row_team_abbreviation.toUpperCase(),
          sport: values.sport as Sport,
          leagueIp: values.league_ip ?? false,
        });
        if (url) setFieldValue('row_team_icon_url', url);
      }
      if (populateColor) {
        const color = getTeamPrimaryColor({
          sport: values.sport,
          teamName: values.row_team_display_name,
          teamAbbreviation: values.row_team_abbreviation,
        });
        if (color) setFieldValue('row_team_bg_color', color);
      }
    }

    if (values.col_team_abbreviation && values.col_team_abbreviation.toUpperCase() !== 'TBD') {
      const populateIcon = colAbbrChanged || (isFirst && !values.col_team_icon_url);
      const populateColor = colAbbrChanged || (isFirst && !values.col_team_bg_color);
      if (populateIcon) {
        const url = resolveTeamImageUrl({
          teamAbbr: values.col_team_abbreviation.toUpperCase(),
          sport: values.sport as Sport,
          leagueIp: values.league_ip ?? false,
        });
        if (url) setFieldValue('col_team_icon_url', url);
      }
      if (populateColor) {
        const color = getTeamPrimaryColor({
          sport: values.sport,
          teamName: values.col_team_display_name,
          teamAbbreviation: values.col_team_abbreviation,
        });
        if (color) setFieldValue('col_team_bg_color', color);
      }
    }
  }, [
    autoLoadTeamAssets,
    values.sport,
    values.league_ip,
    values.row_team_abbreviation,
    values.col_team_abbreviation,
    values.row_team_display_name,
    values.col_team_display_name,
    setFieldValue,
  ]);

  const handleFileUpload = (
    fieldName: 'row_team_icon_url' | 'col_team_icon_url',
    file: File | null
  ) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      alert(`Image file size must be less than ${MAX_UPLOAD_SIZE_MB}MB`);
      return;
    }

    // Convert to data URL for preview
    const reader = new FileReader();
    reader.onload = e => {
      const dataUrl = e.target?.result as string;
      setFieldValue(fieldName, dataUrl);
    };
    reader.onerror = () => {
      alert('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Team Styling</h3>
        <p className="text-sm text-muted-foreground">
          Customize team colors and icons for the squares board display
        </p>
      </div>

      {/* Show teams from Basic tab */}
      {(values.row_team_display_name || values.col_team_display_name) && (
        <div className="border rounded-lg p-4 bg-primary/5 border-primary/20">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              Teams Auto-Populated from Fixture
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Row Team (Team 1):</span>{' '}
              <span className="font-semibold">{values.row_team_display_name || 'Not set'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Column Team (Team 2):</span>{' '}
              <span className="font-semibold">{values.col_team_display_name || 'Not set'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Row Team Styling */}
      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium">
          Row Team (Team 1) Styling
          {values.row_team_display_name && (
            <span className="text-sm font-normal text-muted-foreground ml-2">
              - {values.row_team_display_name}
            </span>
          )}
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="row_team_bg_color">Background Color</Label>
            <div className="flex gap-2">
              <Field
                as={Input}
                id="row_team_bg_color"
                name="row_team_bg_color"
                type="color"
                disabled={disabled}
                className="w-20 h-10"
              />
              <Field
                as={Input}
                name="row_team_bg_color"
                placeholder="#FF0000"
                disabled={disabled}
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">Hex color code (e.g., #FF0000)</p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="row_team_icon_url">Icon/Logo URL</Label>
            <div className="flex gap-2">
              <Field
                as={Input}
                id="row_team_icon_url"
                name="row_team_icon_url"
                placeholder="https://example.com/team-logo.png"
                disabled={disabled}
                className="flex-1"
              />
              <JerseyPickerButton
                onSelect={url => {
                  setFieldValue('row_team_icon_url', url);
                }}
                fieldLabel="Row Team Icon"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => rowTeamFileInputRef.current?.click()}
                title="Upload custom image"
              >
                <Upload className="h-4 w-4" />
              </Button>
              <input
                ref={rowTeamFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0] || null;
                  handleFileUpload('row_team_icon_url', file);
                  // Reset input so same file can be selected again
                  if (e.target) {
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Team logo or icon URL. Use jersey selector to choose from stored jerseys, or upload a
              custom image.
            </p>
          </div>
        </div>

        {values.row_team_icon_url && (
          <div className="mt-2">
            <img
              src={values.row_team_icon_url}
              alt="Row team icon"
              className="h-16 w-16 object-contain border rounded"
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
      </div>

      {/* Column Team Styling */}
      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium">
          Column Team (Team 2) Styling
          {values.col_team_display_name && (
            <span className="text-sm font-normal text-muted-foreground ml-2">
              - {values.col_team_display_name}
            </span>
          )}
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="col_team_bg_color">Background Color</Label>
            <div className="flex gap-2">
              <Field
                as={Input}
                id="col_team_bg_color"
                name="col_team_bg_color"
                type="color"
                disabled={disabled}
                className="w-20 h-10"
              />
              <Field
                as={Input}
                name="col_team_bg_color"
                placeholder="#0000FF"
                disabled={disabled}
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">Hex color code (e.g., #0000FF)</p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="col_team_icon_url">Icon/Logo URL</Label>
            <div className="flex gap-2">
              <Field
                as={Input}
                id="col_team_icon_url"
                name="col_team_icon_url"
                placeholder="https://example.com/team-logo.png"
                disabled={disabled}
                className="flex-1"
              />
              <JerseyPickerButton
                onSelect={url => {
                  setFieldValue('col_team_icon_url', url);
                }}
                fieldLabel="Column Team Icon"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => colTeamFileInputRef.current?.click()}
                title="Upload custom image"
              >
                <Upload className="h-4 w-4" />
              </Button>
              <input
                ref={colTeamFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0] || null;
                  handleFileUpload('col_team_icon_url', file);
                  // Reset input so same file can be selected again
                  if (e.target) {
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Team logo or icon URL. Use jersey selector to choose from stored jerseys, or upload a
              custom image.
            </p>
          </div>
        </div>

        {values.col_team_icon_url && (
          <div className="mt-2">
            <img
              src={values.col_team_icon_url}
              alt="Column team icon"
              className="h-16 w-16 object-contain border rounded"
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Field
          type="checkbox"
          id="auto_team_assets_from_matchup"
          name="auto_team_assets_from_matchup"
          disabled={disabled}
          className="h-4 w-4"
        />
        <Label htmlFor="auto_team_assets_from_matchup" className="cursor-pointer">
          Auto-load team assets from selected fixture
        </Label>
      </div>
      <p className="text-xs text-muted-foreground -mt-4">
        When enabled, selecting a fixture auto-fills team logos and supported team hex colors.
      </p>

      {/* Custom Background Assets Section */}
      <div className="border-t pt-6 mt-6">
        <CustomAssetsSection disabled={disabled} contestId={contestId} tenantCode={tenantCode} />
      </div>
    </div>
  );
}
